<template>
  <div v-if="loaded">
    <ManageSubsidyHeader
      :submitted-at="subsidy.submitted_at"
      :subtitle="subsidyProgram.name"
      :subtitle-subtext="subsidyProgram.description"
      :title="childApplicationData.name"
    />

    <v-container class="d-flex flex-column ga-12">
      <DashboardTasks
        @reload-tasks="loadTasks"
        :tasks="subsidyTasks"
      />

      <template v-if="!subsidyProgram.disable_seats && offerCount > 0">
        <div class="d-flex flex-column ga-4">
          <h2>
            {{ $bct('Offers') + ' 🎉' }}
          </h2>

          <template v-if="childSchema?.meta.parent_layout?.enrollments_instructions">
            <v-card
              class="bg-primary"
              border
              tile
            >
              <v-card-text class="c-white fs-16 fw-500 markdown">
                <MarkdownContent
                  :content="childSchema?.meta.parent_layout.enrollments_instructions"
                />
              </v-card-text>
            </v-card>
          </template>

          <ManageChildSubsidyOfferList
            v-model:processing="processing"
            @changed="handleEnrollmentChange"
            ref="offerList"
            :child-id="subsidy.child_id"
            :subsidy-program="subsidyProgram"
            data-testid="offer-list"
          />
        </div>
      </template>

      <template v-if="displayPreferencesSection">
        <div>
          <v-row>
            <v-col>
              <h2 class="mb-4">
                {{ $bct('Preferences') }}
              </h2>
            </v-col>

            <v-col
              v-if="allowAddPrograms"
              class="text-right"
            >
              <v-btn
                @click="() => $refs.childSubsidyPreferenceList.openAddProgramDialog()"
                color="primary"
                prepend-icon="add"
              >
                {{ $bct('Add provider') }}
              </v-btn>
            </v-col>
          </v-row>

          <ChildSubsidyPreferenceList
            v-model:processing="processing"
            @changed="handleEnrollmentChange"
            ref="childSubsidyPreferenceList"
            :child-application-data="childApplicationData"
            :child-id="subsidy.child_id"
            :child-schema="childSchema"
            :show-add-button="false"
            :show-instructions="false"
            :subsidy="subsidy"
            :subsidy-program="subsidyProgram"
            data-testid="preference-list"
          />
        </div>
      </template>

      <div class="d-flex flex-column ga-4">
        <h2>
          {{ $bct('Application') }}
        </h2>

        <v-card
          v-if="childSchema?.meta.parent_layout?.applications_instructions"
          class="bg-primary"
          border
          tile
        >
          <v-card-text class="c-white fs-16 fw-500 markdown">
            <MarkdownContent :content="childSchema?.meta.parent_layout.applications_instructions" />
          </v-card-text>
        </v-card>

        <v-card
          border
          tile
        >
          <ChildSubsidyReview
            :child-application-data="childApplicationData"
            :value="subsidy"
          />
        </v-card>
      </div>

      <div
        v-if="agreements?.length > 0"
        class="d-flex flex-column ga-4"
      >
        <h2>
          {{ $bct('Agreements') }}
        </h2>

        <AgreementTable
          v-if="agreements"
          @change="loadAgreements"
          :agreements="agreements"
          role="parent"
        />
      </div>
    </v-container>
  </div>

  <v-progress-linear
    v-else
    indeterminate
  />
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import AgreementTable from '@/shared/components/agreement/AgreementTable.vue';
import ChildSubsidyPreferenceList from '@/parent/components/subsidy/ChildSubsidyPreferenceList.vue';
import ChildSubsidyReview from '@/parent/views/child-subsidies/ChildSubsidyReview.vue';
import DashboardTasks from '@/parent/components/dashboards/DashboardTasks.vue';
import { ENROLLMENT_STATUSES } from '@/shared/assets/constants';
import ManageChildSubsidyOfferList from '@/parent/components/subsidy/ManageChildSubsidyOfferList.vue';
import ManageSubsidyHeader from '@/parent/components/subsidy/ManageSubsidyHeader.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import SubsidyApplicationData from '@/shared/services/subsidy/application-data.js';
import useApi from '@/shared/composables/useApi';

const route = useRoute();
const store = useStore();
const { api: Api } = useApi();

const $bct = inject('translator');

const agreements = ref(null);
const allowAddPrograms = ref(false);
const childApplicationData = ref(null);
const childSubsidyPreferenceList = ref(null);
const enrollmentCount = ref(0);
const loadEnrollmentsFinished = ref(false);
const offerCount = ref(0);
const offerList = ref(null);
const processing = ref(false);
const subsidy = ref(null);
const subsidyTasks = ref([]);
const subsidyProgram = ref(null);

const childSchema = computed(() => store.state.schemas[childApplicationData.value?.schema_id]);

const displayPreferencesSection = computed(
  () =>
    subsidy.value?.locked &&
    (allowAddPrograms.value || subsidyProgram.value.enable_parent_enrollments_review) &&
    (enrollmentCount.value > 0 || subsidyProgram.value.enable_automatic_preference_unlock),
);

const loaded = computed(
  () =>
    childApplicationData.value &&
    subsidy.value &&
    subsidyProgram.value &&
    loadEnrollmentsFinished.value,
);

function addProgramsAllowed(enrollments) {
  if (
    subsidyProgram.value?.enable_iep_direct_placement &&
    childApplicationData.value?.individualized_education_plan
  ) {
    return false;
  }

  if (subsidy.value?.preferences_unlocked_at) return true;
  if (!subsidyProgram.value.enable_automatic_preference_unlock) return false;

  // Handle when automatic preferences unlock is enabled
  if (enrollments.length === 0) return true;

  if (!enrollments.some((enrollment) => enrollment.prevents_automatic_preference_unlock))
    return true;

  return false;
}

async function load() {
  processing.value = true;
  const { data: subsidyData } = await Api.parent.subsidy.get(
    store.state.profile?.group_id,
    route.params.subsidyId,
  );
  subsidy.value = subsidyData;

  const { data: subsidyProgramData } = await Api.shared.subsidyProgram.get(
    subsidy.value.subsidy_program_id,
  );
  subsidyProgram.value = subsidyProgramData;

  await loadChildApplicationData();
  await Promise.all([loadEnrollments(), loadTasks(), loadAgreements()]);

  processing.value = false;
}

async function loadChildApplicationData() {
  const response = await SubsidyApplicationData.getChildData(subsidy.value.id);
  childApplicationData.value = response?.data ?? null;
}

async function loadAgreements() {
  const response = await Api.parent.agreement.index({
    member_id: store.state.profile.id,
    subsidy_id: subsidy.value.id,
  });
  agreements.value = response?.data;
}

async function loadEnrollments() {
  const response = await Api.parent.enrollment.index({ child_id: subsidy.value.child_id });

  if (response?.data) {
    const enrollments = response.data;
    const filteredEnrollments = enrollments.filter(
      (enrollment) => enrollment.subsidy_program_id === subsidyProgram.value.id,
    );

    allowAddPrograms.value = addProgramsAllowed(filteredEnrollments);

    const filteredOffers = filteredEnrollments.filter(
      (enrollment) =>
        enrollment.public_status === ENROLLMENT_STATUSES.OFFERED ||
        enrollment.public_status === ENROLLMENT_STATUSES.ACCEPTED,
    );

    offerCount.value = filteredOffers.length;

    enrollmentCount.value = filteredEnrollments.length;
  }
  loadEnrollmentsFinished.value = true;
}

async function loadTasks() {
  const response = await Api.parent.applicationSummary.index({ subsidy_id: subsidy.value.id });
  subsidyTasks.value = response?.data;
}

async function handleEnrollmentChange() {
  await loadEnrollments();
  await loadTasks();
  childSubsidyPreferenceList.value.load();
  offerList.value?.load();
}

onMounted(load);
</script>
